/**
 * TrackVisionSessionPage.jsx — Vukz Performance Report
 * =====================================================
 * Minimal B&W design. Color only for alerts/warnings.
 * Print-ready (A4 PDF layout). Vukz branding header.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSportsLang } from '../contexts/SportsLangContext';
import { getSession, getSessionReport, cancelSession, reprocessSession, pollSessionStatus } from '../services/trackVisionService';
import { normalizeTrackVisionReport } from '../utils/trackVisionReportNormalizer';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft, Loader2, Ban, RefreshCw, Printer, Tag,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════
// PRINT STYLES (injected once)
// ═══════════════════════════════════════════════════════════════════
const PRINT_CSS = `
@media print {
  body * { visibility: hidden; }
  #vukz-report, #vukz-report * { visibility: visible; }
  #vukz-report { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
  .no-print { display: none !important; }
  .page-break { page-break-before: always; }
}
`;

// ═══════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════

const LABELS_I18N = {
  pt: {
    sessionNotFound: 'Sessão não encontrada.',
    sessionLabel: (id) => `Sessão #${id}`,
    cancel: 'Cancelar', reprocess: 'Reprocessar', annotate: 'Anotar',
    confirmCancel: 'Cancelar esta sessão?',
    confirmReprocess: 'Reprocessar com modelo mais recente?',
    analyzing: 'Analisando vídeo...',
    analyzingWait: 'O relatório aparecerá automaticamente ao finalizar.',
    processingFailed: 'Falha no processamento',
    internalError: 'Erro interno. Tente reprocessar.',
    reportNotNormalized: 'Relatório encontrado, mas sem métricas normalizadas.',
    reportFormatError: 'O formato retornado não contém campos esperados. Tente reprocessar.',
    loadingReport: 'Carregando relatório final...',
    reportUnavailable: 'Relatório não disponível. Tente reprocessar.',
    reportSaving: 'Relatório ainda sendo gravado...',
    reportLoadError: 'Erro ao carregar relatório. Atualize a página.',
    sessionLoadError: 'Falha ao carregar sessão.',
    reportTitle: 'Relatório de Performance',
    execSummary: 'Resumo Executivo',
    strength: 'Ponto forte', priority: 'Prioridade',
    keyMetrics: 'Métricas Principais',
    phaseTimeline: 'Timeline das Fases',
    velocityCurve: 'Curva de Velocidade',
    cadenceByPhase: 'Cadência por Fase (spm)',
    gctByPhase: 'Contato com Solo por Fase (ms)',
    phaseAnalysis: 'Análise por Fase',
    thPhase: 'Fase', thDuration: 'Duração', thVelocity: 'Velocidade',
    thCadence: 'Cadência', thGCT: 'GCT', thConf: 'Confiança',
    attention: 'Atenção',
    warnGCT: (ms) => `GCT elevado (${ms}ms) — possível fadiga ou falta de rigidez`,
    warnVelLoss: (pct) => `Perda de velocidade de ${pct.toFixed(1)}%`,
    warnConfidence: (pct) => `Confiança baixa (${pct}%) — vídeo pode ter obstrução`,
    symmetry: 'Simetria',
    asymmetryWarning: 'Assimetria significativa detectada. Recomenda-se avaliação fisioterâpeuta.',
    iqBreakdown: 'Composição do Sprint IQ',
    iqKeys: { spatiotemporal: 'Espaço-temporal', kinematics: 'Cinemática', symmetry: 'Simetria', phase_specific: 'Por fase', posture: 'Postura' },
    actionPlan: 'Plano de Ação',
    videoQuality: 'Qualidade do Vídeo',
    resolution: 'Resolução', camera: 'Câmera', score: 'Score',
    cameraLateral: 'Lateral fixa',
    overlayVideo: 'Vídeo com Overlay',
    sprintIQ: 'Sprint IQ',
    chartVelocity: 'Velocidade',
    // Metric labels
    mVelMax: 'Vel. Máxima', mCadence: 'Cadência', mStride: 'Passada',
    mGCT: 'Contato Solo', mFlight: 'Tempo Voo', mStrides: 'Passadas',
    mVelLoss: 'Perda Vel.', mAsym: 'Assimetria',
    mSymIndex: 'Índice Geral', mSymGCT: 'GCT (E vs D)',
    mSymStride: 'Passada (E vs D)', mSymKnee: 'Joelho (dif.)',
    // Action item categories
    catPhysical: 'Físico', catTechnical: 'Técnico', catPhysio: 'Fisioterapia', catNextAnalysis: 'Próxima análise',
    // Action item texts
    actVelEndurance: 'Speed endurance: séries de 120-150m a 95% para melhorar manutenção de velocidade.',
    actPliometria: 'Pliometria reativa (drop jumps, hurdle hops) para reduzir tempo de contato com solo.',
    actAsymmetry: (pct) => `Investigar assimetria de ${pct}%. Trabalho unilateral de força.`,
    actStride: 'Trabalhar amplitude de passada com marcações progressivas no solo.',
    actNextAnalysis: 'Repetir em 2-4 semanas para acompanhar evolução. Gravar lateral a 60fps.',
  },
  en: {
    sessionNotFound: 'Session not found.',
    sessionLabel: (id) => `Session #${id}`,
    cancel: 'Cancel', reprocess: 'Reprocess', annotate: 'Annotate',
    confirmCancel: 'Cancel this session?',
    confirmReprocess: 'Reprocess with latest model?',
    analyzing: 'Analyzing video...',
    analyzingWait: 'The report will appear automatically when done.',
    processingFailed: 'Processing failed',
    internalError: 'Internal error. Try reprocessing.',
    reportNotNormalized: 'Report found, but without normalized metrics.',
    reportFormatError: 'Returned format does not contain expected fields. Try reprocessing.',
    loadingReport: 'Loading final report...',
    reportUnavailable: 'Report not available. Try reprocessing.',
    reportSaving: 'Report still being saved...',
    reportLoadError: 'Error loading report. Refresh the page.',
    sessionLoadError: 'Failed to load session.',
    reportTitle: 'Performance Report',
    execSummary: 'Executive Summary',
    strength: 'Strength', priority: 'Priority',
    keyMetrics: 'Key Metrics',
    phaseTimeline: 'Phase Timeline',
    velocityCurve: 'Velocity Curve',
    cadenceByPhase: 'Cadence by Phase (spm)',
    gctByPhase: 'Ground Contact by Phase (ms)',
    phaseAnalysis: 'Phase Analysis',
    thPhase: 'Phase', thDuration: 'Duration', thVelocity: 'Velocity',
    thCadence: 'Cadence', thGCT: 'GCT', thConf: 'Confidence',
    attention: 'Attention',
    warnGCT: (ms) => `High GCT (${ms}ms) — possible fatigue or stiffness deficit`,
    warnVelLoss: (pct) => `Velocity loss of ${pct.toFixed(1)}%`,
    warnConfidence: (pct) => `Low confidence (${pct}%) — video may have obstruction`,
    symmetry: 'Symmetry',
    asymmetryWarning: 'Significant asymmetry detected. Physiotherapy evaluation recommended.',
    iqBreakdown: 'Sprint IQ Breakdown',
    iqKeys: { spatiotemporal: 'Spatiotemporal', kinematics: 'Kinematics', symmetry: 'Symmetry', phase_specific: 'Phase-specific', posture: 'Posture' },
    actionPlan: 'Action Plan',
    videoQuality: 'Video Quality',
    resolution: 'Resolution', camera: 'Camera', score: 'Score',
    cameraLateral: 'Fixed lateral',
    overlayVideo: 'Overlay Video',
    sprintIQ: 'Sprint IQ',
    chartVelocity: 'Velocity',
    // Metric labels
    mVelMax: 'Max Vel.', mCadence: 'Cadence', mStride: 'Stride',
    mGCT: 'Ground Contact', mFlight: 'Flight Time', mStrides: 'Strides',
    mVelLoss: 'Vel. Loss', mAsym: 'Asymmetry',
    mSymIndex: 'Overall Index', mSymGCT: 'GCT (L vs R)',
    mSymStride: 'Stride (L vs R)', mSymKnee: 'Knee Angle (diff)',
    // Action item categories
    catPhysical: 'Physical', catTechnical: 'Technical', catPhysio: 'Physiotherapy', catNextAnalysis: 'Next analysis',
    // Action item texts
    actVelEndurance: 'Speed endurance: 120-150m sets at 95% to improve velocity maintenance.',
    actPliometria: 'Reactive plyometrics (drop jumps, hurdle hops) to reduce ground contact time.',
    actAsymmetry: (pct) => `Investigate ${pct}% asymmetry. Unilateral strength work.`,
    actStride: 'Work on stride amplitude with progressive ground markings.',
    actNextAnalysis: 'Repeat in 2-4 weeks to track progress. Record lateral view at 60fps.',
  },
};

export default function TrackVisionSessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const { lang } = useSportsLang();
  const L = LABELS_I18N[lang] || LABELS_I18N.pt;

  // ─── SINGLE SOURCE OF TRUTH ───
  const [pageState, setPageState] = useState({
    loading: true,
    session: null,
    report: null,
    error: null,
  });
  const requestIdRef = React.useRef(0); // AbortController for stale responses

  const { loading, session, report, error } = pageState;

  // Fetch fresh report from dedicated endpoint (single source of truth)
  const fetchFreshReport = useCallback(async (reqId) => {
    try {
      const rRes = await getSessionReport(sessionId);
      const reportData = rRes.data || rRes;
      // Only accept if this is still the latest request
      if (reqId === requestIdRef.current && reportData) {
        setPageState(prev => ({ ...prev, report: reportData }));
      }
      return reportData;
    } catch (err) {
      console.error('[TrackVision] Report fetch error:', err);
      return null;
    }
  }, [sessionId]);

  useEffect(() => {
    // Inject print CSS
    const style = document.createElement('style');
    style.textContent = PRINT_CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    let stopPoll;
    const reqId = ++requestIdRef.current;

    const load = async () => {
      try {
        const sRes = await getSession(sessionId);
        const s = sRes.data || sRes;
        if (reqId !== requestIdRef.current) return; // stale

        if (s.status === 'completed') {
          // Session is done — use inline report immediately, it's the same data
          console.log('[TrackVision] Session loaded, status=completed. s.report type:', typeof s.report, 'keys:', Object.keys(s.report || {}));
          setPageState({
            loading: false,
            session: s,
            report: s.report || null,
            error: s.report ? null : L.reportUnavailable,
          });
        } else if (['processing', 'pending', 'uploaded', 'queued'].includes(s.status)) {
          setPageState({ loading: false, session: s, report: null, error: null });

          stopPoll = pollSessionStatus(sessionId, {
            interval: 4000,
            onUpdate: (upd) => {
              if (reqId !== requestIdRef.current) return;
              // Only update non-terminal statuses (never set completed without report)
              if (!['completed', 'failed', 'cancelled'].includes(upd.status)) {
                setPageState(prev => ({ ...prev, session: { ...prev.session, ...upd } }));
              }
            },
            onComplete: async (upd) => {
              if (reqId !== requestIdRef.current) return;
              if (upd.status === 'completed') {
                // Backend guarantees: status=completed means report is persisted
                // Still wait 1s for any replication lag, then fetch full session
                await new Promise(r => setTimeout(r, 1000));
                try {
                  const freshRes = await getSession(sessionId);
                  const fs = freshRes.data || freshRes;
                  if (reqId !== requestIdRef.current) return;
                  setPageState({
                    loading: false,
                    session: fs,
                    report: fs.report || null,
                    error: fs.report ? null : L.reportSaving,
                  });
                  // If inline report was null (unlikely now), try dedicated endpoint
                  if (!fs.report) {
                    await new Promise(r => setTimeout(r, 1500));
                    await fetchFreshReport(reqId);
                  }
                } catch (e) {
                  setPageState(prev => ({
                    ...prev,
                    session: { ...prev.session, status: 'completed' },
                    error: L.reportLoadError,
                  }));
                }
              } else {
                // failed / cancelled
                setPageState(prev => ({ ...prev, session: { ...prev.session, ...upd } }));
              }
            },
          });
        } else {
          // failed, cancelled, etc
          setPageState({ loading: false, session: s, report: null, error: null });
        }
      } catch (err) {
        console.error('[TrackVision] Load error:', err);
        if (reqId === requestIdRef.current) {
          setPageState({ loading: false, session: null, report: null, error: L.sessionLoadError });
        }
      }
    };
    load();
    return () => { if (stopPoll) stopPoll(); };
  }, [sessionId, fetchFreshReport]);

  const handleCancel = async () => {
    if (!window.confirm(L.confirmCancel)) return;
    await cancelSession(sessionId);
    const r = await getSession(sessionId);
    const s = r.data || r;
    setPageState(prev => ({ ...prev, session: s }));
  };

  const handleReprocess = async () => {
    if (!window.confirm(L.confirmReprocess)) return;
    await reprocessSession(sessionId);
    setPageState(prev => ({
      ...prev,
      session: { ...prev.session, status: 'processing' },
      report: null,
      error: null,
    }));
  };

  // ─── NORMALIZE REPORT (handles all backend versions) ───
  const normalized = useMemo(() => {
    if (!report) return null;
    console.log('[TrackVision] Raw report for normalizer:', typeof report, report);
    const n = normalizeTrackVisionReport(report, session);
    if (!n) console.warn('[TrackVision] Normalizer returned null. Keys:', Object.keys(report || {}));
    else console.log('[TrackVision] Normalized:', { cadenceSpm: n.cadenceSpm, groundContactMs: n.groundContactMs, phases: n.phases.length });
    return n;
  }, [report, session]);

  // Derived chart data from normalized phases
  const cadenceData = useMemo(() => {
    if (!normalized) return [];
    return normalized.phases.filter(p => p.metrics?.avg_cadence_spm).map(p => ({
      phase: p.name.split(' ')[0],
      cadence: p.metrics.avg_cadence_spm,
    }));
  }, [normalized]);

  const gctData = useMemo(() => {
    if (!normalized) return [];
    return normalized.phases.filter(p => p.metrics?.avg_gct_ms).map(p => ({
      phase: p.name.split(' ')[0],
      gct: p.metrics.avg_gct_ms,
    }));
  }, [normalized]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 size={24} className="animate-spin text-black" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-sm text-gray-500">{L.sessionNotFound}</p>
      </div>
    );
  }

  const canCancel = ['pending', 'processing', 'uploaded', 'queued'].includes(session.status);
  const canReprocess = ['failed', 'cancelled', 'completed'].includes(session.status);

  // Shortcut refs from normalized
  const n = normalized;
  const exec = n?.executiveSummary || {};
  const sym = n?.symmetry || {};
  const vq = n?.videoQuality || {};
  const mqs = n?.mqsBreakdown || {};
  const score = n?.movementQualityScore;
  const phases = n?.phases || [];

  return (
    <div className="min-h-screen bg-white">
      {/* ── Actions bar (no-print) ── */}
      <div className="no-print sticky top-0 z-50 bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-3">
        <button onClick={() => navigate('/track-vision/sessions')} className="p-2 hover:bg-gray-100 rounded">
          <ArrowLeft size={16} />
        </button>
        <span className="text-xs text-gray-500 flex-1">{L.sessionLabel(sessionId)}</span>
        {canCancel && (
          <button onClick={handleCancel} className="text-xs px-3 py-1.5 border rounded hover:bg-gray-50 text-red-600 border-red-200 flex items-center gap-1">
            <Ban size={12} /> {L.cancel}
          </button>
        )}
        {canReprocess && (
          <button onClick={handleReprocess} className="text-xs px-3 py-1.5 border rounded hover:bg-gray-50 flex items-center gap-1">
            <RefreshCw size={12} /> {L.reprocess}
          </button>
        )}
        {n && (
          <button onClick={() => window.print()} className="text-xs px-3 py-1.5 border rounded hover:bg-gray-50 flex items-center gap-1">
            <Printer size={12} /> PDF
          </button>
        )}
        {session.status === 'completed' && (
          <Link to={`/track-vision/sessions/${sessionId}/annotate`} className="text-xs px-3 py-1.5 border rounded hover:bg-gray-50 flex items-center gap-1">
            <Tag size={12} /> {L.annotate}
          </Link>
        )}
      </div>

      {/* ── Processing state ── */}
      {['processing', 'pending', 'queued', 'uploaded'].includes(session.status) && (
        <div className="flex flex-col items-center justify-center py-32">
          <Loader2 size={28} className="animate-spin text-black mb-3" />
          <p className="text-sm font-medium">{L.analyzing}</p>
          <p className="text-xs text-gray-400 mt-1">{L.analyzingWait}</p>
        </div>
      )}

      {/* ── Error ── */}
      {session.status === 'failed' && (
        <div className="max-w-3xl mx-auto px-6 py-12 text-center">
          <p className="text-sm text-red-600 font-medium">{L.processingFailed}</p>
          <p className="text-xs text-gray-500 mt-1">{session.error_message || L.internalError}</p>
        </div>
      )}

      {/* ── Report not normalized ── */}
      {session.status === 'completed' && report && !n && (
        <div className="max-w-3xl mx-auto px-6 py-12 text-center">
          <p className="text-sm text-amber-700 font-medium">{L.reportNotNormalized}</p>
          <p className="text-xs text-gray-500 mt-1">{L.reportFormatError}</p>
          <button onClick={handleReprocess} className="mt-3 text-xs px-3 py-1.5 border rounded hover:bg-gray-50 flex items-center gap-1 mx-auto">
            <RefreshCw size={12} /> Reprocessar
          </button>
        </div>
      )}

      {/* ── Report loading / error ── */}
      {session.status === 'completed' && !report && (
        <div className="flex flex-col items-center justify-center py-20">
          {error ? (
            <>
              <p className="text-sm text-gray-600">{error}</p>
              <button onClick={handleReprocess} className="mt-3 text-xs px-3 py-1.5 border rounded hover:bg-gray-50 flex items-center gap-1">
                <RefreshCw size={12} /> {L.reprocess}
              </button>
            </>
          ) : (
            <>
              <Loader2 size={20} className="animate-spin text-gray-400" />
              <span className="text-xs text-gray-400 mt-2">{L.loadingReport}</span>
            </>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          REPORT (printable area) — uses NORMALIZED data only
         ══════════════════════════════════════════════════════════════ */}
      {session.status === 'completed' && n && (
        <div id="vukz-report" className="max-w-3xl mx-auto px-6 py-8 text-black">

          {/* ═══ HEADER ═══ */}
          <header className="flex items-start justify-between mb-8 pb-6 border-b border-black">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <img src="/logo-vukz.png" alt="Vukz" className="h-6" onError={(e) => { e.target.style.display = 'none'; }} />
                <span className="text-xs font-bold tracking-widest uppercase">VUKZ</span>
              </div>
              <h1 className="text-xl font-black tracking-tight">{L.reportTitle}</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {n.meta.eventType || 'Sprint Analysis'} • Sessão #{sessionId} •{' '}
                {session.created_at ? new Date(session.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}
              </p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-black">{score?.toFixed(0) || '—'}</div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">{L.sprintIQ}</div>
            </div>
          </header>

          {/* ═══ EXECUTIVE SUMMARY ═══ */}
          {(exec.topStrength || exec.topImprovement || exec.recommendation) && (
            <section className="mb-8">
              <h2 className="text-xs font-black uppercase tracking-wider mb-3 text-gray-500">{L.execSummary}</h2>
              <div className="space-y-2">
                {exec.topStrength && (
                  <p className="text-sm"><span className="font-bold">{L.strength}:</span> {exec.topStrength}</p>
                )}
                {exec.topImprovement && (
                  <p className="text-sm"><span className="font-bold">{L.priority}:</span> {exec.topImprovement}</p>
                )}
                {exec.recommendation && (
                  <p className="text-sm bg-gray-50 border-l-2 border-black px-3 py-2">{exec.recommendation}</p>
                )}
              </div>
            </section>
          )}

          {/* ═══ KEY METRICS ═══ */}
          <section className="mb-8">
            <h2 className="text-xs font-black uppercase tracking-wider mb-3 text-gray-500">{L.keyMetrics}</h2>
            <div className="grid grid-cols-4 gap-px bg-gray-200 border border-gray-200">
              <Metric label={L.mVelMax} value={n.velocityPeakMs?.toFixed(1)} unit="m/s" sub={n.velocityPeakMs ? `${(n.velocityPeakMs * 3.6).toFixed(1)} km/h` : null} />
              <Metric label={L.mCadence} value={n.cadenceSpm?.toFixed(0)} unit="spm" />
              <Metric label={L.mStride} value={n.strideLength?.toFixed(2)} unit="m" />
              <Metric label={L.mGCT} value={n.groundContactMs} unit="ms" warn={n.groundContactMs > 110} />
              <Metric label={L.mFlight} value={n.flightTimeMs} unit="ms" />
              <Metric label={L.mStrides} value={n.totalStrides} unit="" />
              <Metric label={L.mVelLoss} value={n.velocityDecrementPct?.toFixed(1)} unit="%" warn={n.velocityDecrementPct > 5} />
              <Metric label={L.mAsym} value={n.asymmetryPct?.toFixed(1)} unit="%" warn={n.asymmetryPct > 8} />
            </div>
          </section>

          {/* ═══ PHASE TIMELINE ═══ */}
          {phases.length > 0 && phases[0].endTime != null && (
            <section className="mb-8">
              <h2 className="text-xs font-black uppercase tracking-wider mb-3 text-gray-500">{L.phaseTimeline}</h2>
              <div className="flex h-8 rounded overflow-hidden border border-gray-200">
                {phases.map((p, i) => {
                  const totalDuration = phases[phases.length - 1]?.endTime || 1;
                  const widthPct = ((p.duration || 0) / totalDuration) * 100;
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-center text-white text-[9px] font-bold truncate px-1"
                      style={{ width: `${widthPct}%`, backgroundColor: p.color, minWidth: '30px' }}
                      title={`${p.name}: ${p.startTime?.toFixed(1)}s → ${p.endTime?.toFixed(1)}s`}
                    >
                      {widthPct > 12 ? p.name.split(' ')[0] : ''}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[9px] text-gray-400 mt-1 px-0.5">
                <span>0.0s</span>
                {phases[phases.length - 1]?.endTime && (
                  <span>{phases[phases.length - 1].endTime.toFixed(1)}s</span>
                )}
              </div>
            </section>
          )}

          {/* ═══ VELOCITY CHART ═══ */}
          {n.velocityCurve.length > 1 && (
            <section className="mb-8">
              <h2 className="text-xs font-black uppercase tracking-wider mb-3 text-gray-500">{L.velocityCurve}</h2>
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={n.velocityCurve} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#e5e7eb" />
                  <XAxis dataKey="distance" tick={{ fontSize: 9, fill: '#6b7280' }} unit="m" />
                  <YAxis domain={['dataMin - 0.5', 'dataMax + 0.5']} tick={{ fontSize: 9, fill: '#6b7280' }} width={32} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4, border: '1px solid #e5e7eb' }} formatter={(v) => [`${v.toFixed(2)} m/s`, L.chartVelocity]} />
                  <Area type="monotone" dataKey="velocity" stroke="#000" strokeWidth={2} fill="#f3f4f6" dot={{ r: 3, fill: '#000' }} />
                </AreaChart>
              </ResponsiveContainer>
            </section>
          )}

          {/* ═══ CADENCE + GCT ═══ */}
          {(cadenceData.length > 1 || gctData.length > 1) && (
            <section className="mb-8 grid grid-cols-2 gap-6">
              {cadenceData.length > 1 && (
                <div>
                  <h3 className="text-[10px] font-bold uppercase tracking-wider mb-2 text-gray-500">{L.cadenceByPhase}</h3>
                  <ResponsiveContainer width="100%" height={100}>
                    <BarChart data={cadenceData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                      <XAxis dataKey="phase" tick={{ fontSize: 8, fill: '#6b7280' }} />
                      <YAxis domain={[0, 'dataMax + 20']} tick={{ fontSize: 8, fill: '#9ca3af' }} width={25} />
                      <Bar dataKey="cadence" fill="#000" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              {gctData.length > 1 && (
                <div>
                  <h3 className="text-[10px] font-bold uppercase tracking-wider mb-2 text-gray-500">{L.gctByPhase}</h3>
                  <ResponsiveContainer width="100%" height={100}>
                    <BarChart data={gctData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                      <XAxis dataKey="phase" tick={{ fontSize: 8, fill: '#6b7280' }} />
                      <YAxis domain={[0, 'dataMax + 15']} tick={{ fontSize: 8, fill: '#9ca3af' }} width={25} />
                      <Bar dataKey="gct" fill="#374151" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </section>
          )}

          {/* ═══ PHASES TABLE ═══ */}
          {phases.length > 0 && (
            <section className="mb-8">
              <h2 className="text-xs font-black uppercase tracking-wider mb-3 text-gray-500">{L.phaseAnalysis}</h2>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left py-1.5 font-bold">{L.thPhase}</th>
                    <th className="text-right py-1.5 font-bold">{L.thDuration}</th>
                    <th className="text-right py-1.5 font-bold">{L.thVelocity}</th>
                    <th className="text-right py-1.5 font-bold">{L.thCadence}</th>
                    <th className="text-right py-1.5 font-bold">{L.thGCT}</th>
                    <th className="text-right py-1.5 font-bold">{L.thConf}</th>
                  </tr>
                </thead>
                <tbody>
                  {phases.map((p, i) => {
                    const m = p.metrics || {};
                    const vel = m.peak_velocity_ms || m.velocity_at_30m_ms || m.velocity_at_10m_ms || m.finish_velocity_ms;
                    const conf = p.confidence ? Math.round(p.confidence * 100) : null;
                    return (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-1.5 font-semibold">{p.name}</td>
                        <td className="py-1.5 text-right">{p.duration?.toFixed(2)}s</td>
                        <td className="py-1.5 text-right">{vel ? `${vel.toFixed(1)} m/s` : '—'}</td>
                        <td className="py-1.5 text-right">{m.avg_cadence_spm ? `${m.avg_cadence_spm}` : '—'}</td>
                        <td className="py-1.5 text-right">
                          <span className={m.avg_gct_ms > 110 ? 'font-bold text-red-600' : ''}>
                            {m.avg_gct_ms ? `${m.avg_gct_ms}` : '—'}
                          </span>
                        </td>
                        <td className="py-1.5 text-right">
                          <span className={conf && conf < 80 ? 'text-amber-600 font-bold' : ''}>
                            {conf ? `${conf}%` : '—'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Phase observations — only if there are warnings */}
              {phases.some(p => {
                const m = p.metrics || {};
                return m.avg_gct_ms > 110 || m.velocity_loss_pct > 5 || (p.confidence && p.confidence < 0.8);
              }) && (
                <div className="mt-3 p-3 bg-gray-50 border-l-2 border-amber-500">
                  <p className="text-[10px] font-bold uppercase text-amber-600 mb-1">{L.attention}</p>
                  {phases.map((p, i) => {
                    const m = p.metrics || {};
                    const warnings = [];
                    if (m.avg_gct_ms > 110) warnings.push(L.warnGCT(m.avg_gct_ms));
                    if (m.velocity_loss_pct > 5) warnings.push(L.warnVelLoss(m));
                    if (p.confidence && p.confidence < 0.8) warnings.push(L.warnConfidence(Math.round(p.confidence * 100)));
                    if (warnings.length === 0) return null;
                    return (
                      <div key={i} className="text-xs mb-1">
                        <span className="font-bold">{p.name}:</span>{' '}
                        {warnings.join('; ')}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* ═══ SYMMETRY ═══ */}
          {sym.asymmetryIndex != null && (
            <section className="mb-8">
              <h2 className="text-xs font-black uppercase tracking-wider mb-3 text-gray-500">{L.symmetry}</h2>
              <div className="grid grid-cols-4 gap-px bg-gray-200 border border-gray-200">
                <Metric label={L.mSymIndex} value={sym.asymmetryIndex?.toFixed(1)} unit="%" warn={sym.asymmetryIndex > 8} />
                <Metric label={L.mSymGCT} value={sym.gctAsymmetryPct?.toFixed(1)} unit="%" warn={sym.gctAsymmetryPct > 5} />
                <Metric label={L.mSymStride} value={sym.strideLengthAsymmetryPct?.toFixed(1)} unit="%" warn={sym.strideLengthAsymmetryPct > 3} />
                <Metric label={L.mSymKnee} value={sym.kneeAngleDiffDeg?.toFixed(1)} unit="°" warn={sym.kneeAngleDiffDeg > 5} />
              </div>
              {sym.riskFlag && (
                <p className="text-xs mt-2 p-2 bg-amber-50 border-l-2 border-amber-500 text-amber-800">
                  {L.asymmetryWarning}
                </p>
              )}
            </section>
          )}

          {/* ═══ IQ BREAKDOWN ═══ */}
          {mqs.components && (
            <section className="mb-8">
              <h2 className="text-xs font-black uppercase tracking-wider mb-3 text-gray-500">{L.iqBreakdown}</h2>
              <div className="space-y-1.5">
                {Object.entries(mqs.components).map(([key, comp]) => (
                  <div key={key} className="flex items-center gap-3">
                    <span className="text-[11px] w-28 text-right text-gray-600">
                      {L.iqKeys[key] || key}
                    </span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${comp.score}%`, backgroundColor: comp.score >= 80 ? '#000' : comp.score >= 60 ? '#6b7280' : '#dc2626' }} />
                    </div>
                    <span className={`text-[11px] font-bold w-8 ${comp.score < 60 ? 'text-red-600' : ''}`}>{comp.score?.toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ═══ ACTION PLAN ═══ */}
          <section className="mb-8">
            <h2 className="text-xs font-black uppercase tracking-wider mb-3 text-gray-500">{L.actionPlan}</h2>
            <div className="space-y-2">
              {n.velocityDecrementPct > 5 && (
                <ActionItem category={L.catPhysical} text={L.actVelEndurance} />
              )}
              {n.groundContactMs > 105 && (
                <ActionItem category={L.catTechnical} text={L.actPliometria} />
              )}
              {n.asymmetryPct > 8 && (
                <ActionItem category={L.catPhysio} text={L.actAsymmetry(n.asymmetryPct.toFixed(1))} warn />
              )}
              {n.strideLength && n.strideLength < 2.2 && (
                <ActionItem category={L.catTechnical} text={L.actStride} />
              )}
              <ActionItem category={L.catNextAnalysis} text={L.actNextAnalysis} />
            </div>
          </section>

          {/* ═══ VIDEO QUALITY ═══ */}
          {vq.score != null && (
            <section className="mb-8">
              <h2 className="text-xs font-black uppercase tracking-wider mb-3 text-gray-500">{L.videoQuality}</h2>
              <div className="text-xs text-gray-600 flex flex-wrap gap-x-6 gap-y-1">
                <span>FPS: <strong>{vq.fpsDetected || '—'}</strong></span>
                <span>{L.resolution}: <strong>{vq.resolution || '—'}</strong></span>
                <span>{L.camera}: <strong>{vq.cameraType === 'fixed_lateral' ? L.cameraLateral : vq.cameraType || '—'}</strong></span>
                <span>{L.score}: <strong className={vq.score < 70 ? 'text-amber-600' : ''}>{vq.score?.toFixed(0)}%</strong></span>
              </div>
              {vq.warnings?.length > 0 && (
                <div className="mt-2 text-xs text-amber-700">
                  {vq.warnings.map((w, i) => <p key={i}>⚠ {w}</p>)}
                </div>
              )}
            </section>
          )}

          {/* ═══ FOOTER ═══ */}
          <footer className="pt-6 mt-8 border-t border-gray-300 flex items-center justify-between text-[9px] text-gray-400">
            <span>Pipeline v{n.meta.pipelineVersion || '—'} • Modelo {n.meta.modelVersion || '—'} • Benchmark {n.meta.benchmarkVersion || '—'}</span>
            <span>{n.meta.generatedAt ? new Date(n.meta.generatedAt).toLocaleString('pt-BR') : ''}</span>
          </footer>

        </div>
      )}

      {/* Overlay video (no-print) */}
      {session.overlay_blob_url && (
        <div className="no-print max-w-3xl mx-auto px-6 pb-8">
          <h2 className="text-xs font-black uppercase tracking-wider mb-3 text-gray-500">{L.overlayVideo}</h2>
          <video controls className="w-full rounded border" style={{ maxHeight: 400 }}>
            <source src={session.overlay_blob_url} type="video/mp4" />
          </video>
        </div>
      )}
    </div>
  );
}

/* ─── Sub-components ────────────────────────────────────────────────── */

function Metric({ label, value, unit, sub, warn }) {
  if (value == null) return <div className="bg-white p-3 text-center"><span className="text-gray-300">—</span></div>;
  return (
    <div className="bg-white p-3 text-center">
      <div className={`text-lg font-black ${warn ? 'text-red-600' : ''}`}>
        {value}<span className="text-xs font-normal text-gray-400"> {unit}</span>
      </div>
      <div className="text-[9px] text-gray-500 uppercase tracking-wide">{label}</div>
      {sub && <div className="text-[8px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function ActionItem({ category, text, warn }) {
  return (
    <div className={`flex items-start gap-2 text-sm ${warn ? 'bg-amber-50 p-2 border-l-2 border-amber-500' : ''}`}>
      <span className="text-[10px] font-black uppercase text-gray-400 w-24 shrink-0 pt-0.5">{category}</span>
      <span className="text-gray-800">{text}</span>
    </div>
  );
}
